import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import PortalShell, { StatusPill } from "@/components/PortalShell";
import PhotoUploader from "@/components/PhotoUploader";
import Timeline from "@/components/Timeline";
import ReportDetail from "@/components/ReportDetail";
import Lightbox from "@/components/Lightbox";
import { ChevronRight, ChevronDown, Plane, X, Car, LayoutDashboard, FileText, Images, Box, FolderOpen, CalendarDays, Wrench, MessageSquare, Receipt, Download, CalendarClock, ScrollText, HeartPulse } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const Card = ({ children, ...p }) => <div className="card-soft" style={{ padding: 22 }} {...p}>{children}</div>;

export default function Portal() {
  const { user, checkAuth } = useAuth();
  const [props, setProps] = useState([]);
  const [pid, setPid] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [data, setData] = useState({ reports: [], documents: [], invoices: [], issues: [], arrivals: [], messages: [], timeline: [], quotes: [], contracts: [], healthchecks: [] });
  const [visits, setVisits] = useState([]);

  const prop = props.find((p) => p.property_id === pid);
  const [welcome, setWelcome] = useState(false);
  useEffect(() => { if (user) setWelcome(!!user.first_login); }, [user]);
  const dismissWelcome = async () => { setWelcome(false); if (user?.first_login) { try { await api.post("/auth/seen-welcome"); checkAuth(); } catch {} } };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/properties");
        setProps(data);
        if (data.length) setPid(data[0].property_id);
      } catch (e) { toast.error(apiErr(e)); }
    })();
  }, []);

  useEffect(() => { api.get("/my-visits").then(({ data }) => setVisits(data)).catch(() => {}); }, []);

  const loadAll = useCallback(async (id) => {
    if (!id) return;
    try {
      const [r, d, i, is, a, m, t, qz, ct, hc] = await Promise.all([
        api.get(`/reports?property_id=${id}`), api.get(`/documents?property_id=${id}`),
        api.get(`/invoices?property_id=${id}`), api.get(`/issues?property_id=${id}`),
        api.get(`/arrivals?property_id=${id}`), api.get(`/messages?property_id=${id}`),
        api.get(`/timeline?property_id=${id}`), api.get(`/quotes?property_id=${id}`),
        api.get(`/contracts?property_id=${id}`),
        api.get(`/health-checks?property_id=${id}`),
      ]);
      setData({ reports: r.data, documents: d.data, invoices: i.data, issues: is.data, arrivals: a.data, messages: m.data, timeline: t.data, quotes: qz.data, contracts: ct.data, healthchecks: hc.data });
    } catch (e) { toast.error(apiErr(e)); }
  }, []);

  useEffect(() => { loadAll(pid); }, [pid, loadAll]);

  const [unread, setUnread] = useState(0);
  const [unreadBy, setUnreadBy] = useState({});
  const loadUnread = useCallback(() => { api.get("/messages/unread").then((r) => { setUnread(r.data.total || 0); setUnreadBy(r.data.by_property || {}); }).catch(() => {}); }, []);
  useEffect(() => { loadUnread(); }, [loadUnread]);
  useEffect(() => { if (tab === "messages" && pid) { api.post("/messages/read", { property_id: pid }).then(() => loadUnread()).catch(() => {}); } }, [tab, pid, loadUnread]);
  useEffect(() => { const t = new URLSearchParams(window.location.search).get("tab"); if (t) setTab(t); }, []);
  useEffect(() => { if (tab === "messages" && pid) { api.post("/messages/read", { property_id: pid }).then(() => loadUnread()).catch(() => {}); } }, [tab, pid, loadUnread]);

  const navItems = [
    { key: "dashboard", label: "Dashboard", icon: <LayoutDashboard size={16} />, group: "Overview" },
    { key: "timeline", label: "Timeline", icon: <CalendarClock size={16} />, group: "Overview" },
    { key: "arrivals", label: "Arrivals & trips", icon: <CalendarDays size={16} />, group: "Overview" },
    { key: "reports", label: "Reports", icon: <FileText size={16} />, group: "Property" },
    { key: "health", label: "Health check", icon: <HeartPulse size={16} />, group: "Property", badge: tab === "health" ? 0 : (data.healthchecks || []).length },
    { key: "media", label: "Photos & media", icon: <Images size={16} />, group: "Property" },
    { key: "twin", label: "Digital twin", icon: <Box size={16} />, group: "Property" },
    { key: "issues", label: "Issues & approvals", icon: <Wrench size={16} />, group: "Property" },
    { key: "quotes", label: "Quotes", icon: <Receipt size={16} />, group: "Financials" },
    { key: "contracts", label: "Contracts", icon: <ScrollText size={16} />, group: "Financials" },
    { key: "invoices", label: "Invoices", icon: <Receipt size={16} />, group: "Financials", badge: tab === "invoices" ? 0 : data.invoices.filter((i) => i.status && i.status !== "paid").length },
    { key: "messages", label: "Messages", icon: <MessageSquare size={16} />, group: "Communications", badge: tab === "messages" ? 0 : unread },
    { key: "documents", label: "Documents", icon: <FolderOpen size={16} />, group: "Communications" },
  ];

  const payInvoice = async (inv) => {
    // Pre-open a tab within the user gesture so the PWA/standalone view isn't trapped on Stripe with no way back.
    const payTab = window.open("", "_blank");
    try {
      const { data } = await api.post("/payments/checkout", { invoice_id: inv.invoice_id, origin_url: window.location.origin });
      if (payTab) payTab.location = data.checkout_url;
      else window.location.href = data.checkout_url;
    } catch (e) {
      if (payTab) payTab.close();
      toast.error(apiErr(e));
    }
  };

  const raiseIssue = async (title, description, photos = []) => {
    try { await api.post("/issues", { property_id: pid, title, description, photos }); toast.success("Issue raised"); loadAll(pid); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const decide = async (issue_id, approve) => {
    try { await api.post(`/issues/${issue_id}/approve`, { approve }); toast.success(approve ? "Approved" : "Declined"); loadAll(pid); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const sendMsg = async (body) => {
    try { await api.post("/messages", { property_id: pid, body }); loadAll(pid); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addArrival = async (payload) => {
    try { await api.post("/arrivals", { property_id: pid, ...payload }); toast.success("Trip added"); loadAll(pid); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const cancelArrival = async (id) => {
    if (!window.confirm("Cancel this trip?")) return;
    try { await api.delete(`/arrivals/${id}`); toast.success("Trip cancelled"); loadAll(pid); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const markAllRead = async () => {
    try { await api.post("/messages/read-all"); loadUnread(); toast.success("All messages marked as read"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const editArrival = async (id, payload) => {
    try { await api.patch(`/arrivals/${id}`, payload); toast.success("Trip updated"); loadAll(pid); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const welcomeCard = welcome && (
    <div data-testid="welcome-card" style={{ position: "relative", background: "#1a1712", color: "#FBF8F2", borderRadius: 14, padding: "24px 26px", marginBottom: 26, border: "1px solid rgba(199,154,75,.35)" }}>
      <button onClick={dismissWelcome} data-testid="welcome-dismiss" aria-label="Dismiss" style={{ position: "absolute", top: 14, right: 14, background: "none", border: "none", color: "#8b8478", cursor: "pointer" }}><X size={18} /></button>
      <div style={{ fontFamily: "var(--serif)", fontStyle: "italic", color: "#C79A4B", fontSize: 14, marginBottom: 6 }}>Effortless ownership.</div>
      <h2 style={{ fontFamily: "var(--serif)", fontSize: 24, fontWeight: 400, margin: "0 0 8px" }}>Welcome to Solvana{prop?.owner_name ? `, ${prop.owner_name.split(" ")[0]}` : ""}</h2>
      <p style={{ color: "#c9b487", fontSize: 14, lineHeight: 1.7, maxWidth: "62ch" }}>This is your private portal. After every visit we log a dated report with photos, and you'll find updates, invoices and your digital twin here. Explore the menu, start with your Dashboard and Timeline.</p>
    </div>
  );

  if (!props.length) {
    return <PortalShell nav={navItems} active={tab} onNav={setTab} title="Your portal">
      {welcomeCard}
      <Card><p style={{ color: "var(--muted)" }}>No property is linked to your account yet. Your Solvana team will set this up shortly.</p></Card>
    </PortalShell>;
  }

  const bellItems = Object.entries(unreadBy).map(([bpid, count]) => ({ pid: bpid, count, name: (props.find((p) => p.property_id === bpid) || {}).name || "Property" }));
  const bell = { count: unread, items: bellItems, onSelect: (bpid) => { setPid(bpid); setTab("messages"); } };

  return (
    <PortalShell nav={navItems} active={tab} onNav={setTab} title={prop ? prop.name : "Portal"} bell={bell}>
      {props.length > 1 && (
        <select className="inp" style={{ maxWidth: 280, marginTop: -14, marginBottom: 24 }} value={pid || ""} onChange={(e) => setPid(e.target.value)}>
          {props.map((p) => <option key={p.property_id} value={p.property_id}>{p.name}</option>)}
        </select>
      )}
      {prop && <div style={{ marginTop: -14, marginBottom: 26, color: "var(--muted)", fontSize: 14 }}>{prop.location} · <span style={{ color: "var(--honey)" }}>{prop.tier} plan</span></div>}

      {welcomeCard}

      {tab === "dashboard" && <Dashboard prop={prop} data={data} setTab={setTab} visits={visits} />}
      {tab === "timeline" && <div><div className="lbl" style={{ marginBottom: 16 }}>Property timeline</div><CalendarSubscribe propertyId={prop.property_id} /><Timeline items={data.timeline} onChanged={() => loadAll(pid)} /></div>}
      {tab === "quotes" && <Quotes quotes={data.quotes} reload={() => loadAll(pid)} />}
      {tab === "contracts" && <ContractsTab contracts={data.contracts} />}
      {tab === "reports" && <Reports reports={data.reports} />}
      {tab === "health" && <ClientHealthChecks items={data.healthchecks} />}
      {tab === "media" && <Media reports={data.reports} prop={prop} />}
      {tab === "twin" && <Twin prop={prop} />}
      {tab === "documents" && <Documents documents={data.documents} />}
      {tab === "arrivals" && <Arrivals arrivals={data.arrivals} addArrival={addArrival} cancelArrival={cancelArrival} editArrival={editArrival} />}
      {tab === "issues" && <Issues issues={data.issues} raiseIssue={raiseIssue} decide={decide} />}
      {tab === "messages" && <Messages messages={data.messages} sendMsg={sendMsg} markAllRead={markAllRead} />}
      {tab === "invoices" && <Invoices invoices={data.invoices} payInvoice={payInvoice} />}
    </PortalShell>
  );
}

function Dashboard({ prop, data, setTab, visits = [] }) {
  const unpaid = data.invoices.filter((i) => i.status === "unpaid");
  const pendingApprovals = data.issues.filter((i) => i.quote_amount && !i.approval);
  const latest = data.reports[0];
  const [openReport, setOpenReport] = useState(null);
  const recent = (data.timeline || []).slice(0, 6);
  const fmtD = (d) => { const dt = new Date(d); return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" }); };
  return (
    <div style={{ display: "grid", gap: 24 }}>
      {prop?.cover_image && (
        <div style={{ position: "relative", height: 260, overflow: "hidden", borderRadius: 2 }}>
          <img src={prop.cover_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,transparent,rgba(33,29,24,.6))" }} />
          <div style={{ position: "absolute", bottom: 22, left: 24, color: "#fff" }}>
            <span className="lbl" style={{ color: "var(--sand)" }}>{prop.location}</span>
            <div className="font-serif-c" style={{ fontSize: 34, fontWeight: 300 }}>{prop.name}</div>
          </div>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 18 }}>
        <Card><div className="lbl-m">Latest visit</div><div className="font-serif-c" style={{ fontSize: 26, margin: "8px 0" }}>{latest ? latest.title : "-"}</div>{latest && <StatusPill status={latest.status} />}</Card>
        <Card><div className="lbl-m">Open issues</div><div className="font-serif-c" style={{ fontSize: 40, margin: "6px 0" }}>{data.issues.filter((i) => i.status !== "resolved").length}</div><button className="link-u" onClick={() => setTab("issues")}>View</button></Card>
        <Card><div className="lbl-m">Unpaid invoices</div><div className="font-serif-c" style={{ fontSize: 40, margin: "6px 0" }}>{unpaid.length}</div><button className="link-u" onClick={() => setTab("invoices")}>Pay now</button></Card>
        <Card><div className="lbl-m">Quotes to review</div><div className="font-serif-c" style={{ fontSize: 40, margin: "6px 0" }} data-testid="dashboard-quote-count">{data.quotes.filter((q) => q.status === "sent").length}</div><button className="link-u" onClick={() => setTab("quotes")}>View</button></Card>
      </div>

      <Card>
        <div className="lbl" style={{ marginBottom: 4 }}>Recent activity</div>
        <div style={{ display: "grid", gap: 0 }} data-testid="recent-activity">
          {recent.length === 0 && <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 10 }}>Nothing recorded yet.</p>}
          {recent.map((it, idx) => (
            <button key={it.id || idx} data-testid="recent-activity-item" onClick={() => (it.kind === "report" ? setOpenReport(it.id) : setTab("timeline"))} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 2px", background: "none", border: "none", borderBottom: idx < recent.length - 1 ? "1px solid var(--line)" : "none", cursor: "pointer", textAlign: "left", width: "100%", fontFamily: "var(--sans)" }}>
              <span style={{ minWidth: 0 }}>
                <span className="lbl-m" style={{ color: "var(--honey)" }}>{it.type}</span>
                <span style={{ display: "block", fontSize: 14, color: "var(--ink)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</span>
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <span style={{ fontSize: 12, color: "var(--muted)" }}>{fmtD(it.date)}</span>
                <ChevronRight size={16} style={{ color: "var(--muted)" }} />
              </span>
            </button>
          ))}
        </div>
      </Card>

      {pendingApprovals.length > 0 && (
        <Card style={{ borderLeft: "3px solid var(--honey)" }}>
          <div className="lbl">Needs your approval</div>
          {pendingApprovals.map((i) => (
            <div key={i.issue_id} style={{ marginTop: 10 }}>
              <b>{i.title}</b>, €{i.quote_amount}. <button className="link-u" onClick={() => setTab("issues")}>Review</button>
            </div>
          ))}
        </Card>
      )}
      {data.quotes.filter((q) => q.status === "sent").length > 0 && (
        <Card style={{ borderLeft: "3px solid var(--honey)" }} data-testid="dashboard-quotes">
          <div className="lbl">Service plan quotes</div>
          {data.quotes.filter((q) => q.status === "sent").map((q) => (
            <div key={q.quote_id} style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <span><b>{q.tier}</b> · €{Number(q.total || 0).toFixed(2)}/month</span>
              <button className="link-u" onClick={() => setTab("quotes")}>Review &amp; accept</button>
            </div>
          ))}
        </Card>
      )}
      {visits.length > 0 && (
        <Card data-testid="dashboard-visits">
          <div className="lbl">Upcoming visits</div>
          <div style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 12px", maxWidth: "56ch" }}>When our team and trusted contractors are scheduled at your home.</div>
          {visits.map((v, idx) => (
            <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 2px", borderBottom: idx < visits.length - 1 ? "1px solid var(--line)" : "none" }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14, color: "var(--ink)" }}>{v.title}</span>
                {v.trade && <span className="lbl-m" style={{ color: "var(--honey)" }}>{v.trade}</span>}
              </span>
              <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                <span style={{ fontSize: 13, color: "var(--muted)" }}>{v.date ? new Date(v.date).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "2-digit", year: "2-digit" }) : "To be scheduled"}</span>
                {v.time && <span className="lbl-m" style={{ color: "var(--muted)" }}>{v.time}</span>}
              </span>
            </div>
          ))}
        </Card>
      )}
      {openReport && <ReportDetail reportId={openReport} onClose={() => setOpenReport(null)} onChanged={() => {}} />}
    </div>
  );
}

function CalendarSubscribe({ propertyId }) {
  const [link, setLink] = useState(null);
  useEffect(() => { let ok = true; (async () => { try { const { data } = await api.get(`/properties/${propertyId}/calendar-link`); if (ok) setLink(data); } catch { /* ignore */ } })(); return () => { ok = false; }; }, [propertyId]);
  if (!link) return null;
  const abs = `${process.env.REACT_APP_BACKEND_URL}${link.path}`;
  const webcal = abs.replace(/^https?:/, "webcal:");
  const gcal = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`;
  return (
    <div className="card-soft" style={{ padding: 18, marginBottom: 20 }} data-testid="calendar-subscribe">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div>
          <div className="lbl">Add visits to your own calendar</div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 460 }}>Subscribe once, every visit we schedule for your property appears automatically in your phone or computer calendar.</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <a className="btn-ink" style={{ padding: "8px 14px" }} href={webcal} data-testid="subscribe-apple">Subscribe (Apple / Outlook)</a>
          <a className="btn-outline" style={{ padding: "8px 14px" }} href={gcal} target="_blank" rel="noreferrer" data-testid="subscribe-google">Add to Google</a>
          <a className="btn-outline" style={{ padding: "8px 14px" }} href={abs} data-testid="subscribe-download">Download .ics</a>
        </div>
      </div>
    </div>
  );
}

function ContractsTab({ contracts }) {
  const BE = process.env.REACT_APP_BACKEND_URL;
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div className="lbl" style={{ marginBottom: 4 }}>Your service agreements</div>
      {contracts.map((c) => (
        <div key={c.contract_id} className="card-soft" style={{ padding: 20 }} data-testid="portal-contract-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <span className="lbl-m" style={{ color: "var(--honey)" }}>{c.number} · {c.tier}</span>
              <div style={{ fontSize: 20, fontWeight: 500 }}>€{(c.monthly_fee || 0).toFixed(2)}<span style={{ fontSize: 13, color: "var(--muted)" }}> / month</span></div>
              <div className="lbl-m" style={{ marginTop: 4 }}>Effective {c.effective_date}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
              <StatusPill status={c.status} />
              <a href={`${BE}/api/contracts/${c.contract_id}/pdf`} target="_blank" rel="noreferrer" className="link-u" data-testid="portal-contract-pdf" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 6 }}><Download size={13} /> View &amp; print PDF</a>
            </div>
          </div>
        </div>
      ))}
      {contracts.length === 0 && <p style={{ color: "var(--muted)" }}>No service agreement yet. Your Solvana team will send one for you to review and sign.</p>}
    </div>
  );
}

function Quotes({ quotes, reload }) {
  const act = async (id, action) => { try { await api.post(`/quotes/${id}/${action}`); toast.success(action === "accept" ? "Quote accepted, your plan is active" : "Quote declined"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div style={{ display: "grid", gap: 14 }}>
      {quotes.map((q) => (
        <div key={q.quote_id} className="card-soft" style={{ padding: 20 }} data-testid="quote-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><span className="lbl-m" style={{ color: "var(--honey)" }}>{q.tier} · {q.visits_per_month} visits/mo</span><div style={{ fontSize: 22, fontWeight: 500 }}>€{q.total}<span style={{ fontSize: 13, color: "var(--muted)" }}> / month</span></div>{q.discount_pct > 0 && <div style={{ fontSize: 12, color: "#6b4e8a", marginTop: 2 }} data-testid="quote-discount-note">{q.discount_pct}% discount applied · was €{Number(q.subtotal || 0).toFixed(0)}/mo</div>}</div><StatusPill status={q.status} /></div>
          <div style={{ marginTop: 10, fontSize: 13 }}>{q.line_items?.map((l, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--line)", padding: "5px 0" }}><span>{l.label}{l.qty > 1 ? ` ×${l.qty}` : ""}</span><span>€{l.amount}</span></div>)}{q.discount_pct > 0 && <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", color: "#6b4e8a" }}><span>Discount ({q.discount_pct}%)</span><span>-€{Number(q.discount_amount || 0).toFixed(2)}</span></div>}</div>
          {q.status === "sent" && <div style={{ display: "flex", gap: 8, marginTop: 14 }}><button className="btn-ink" onClick={() => act(q.quote_id, "accept")} data-testid="accept-quote-btn">Accept plan</button><button className="btn-outline" onClick={() => act(q.quote_id, "decline")} data-testid="decline-quote-btn">Decline</button></div>}
        </div>
      ))}
      {quotes.length === 0 && <p style={{ color: "var(--muted)" }}>No quotes yet.</p>}
    </div>
  );
}

function ReportCard({ r, setLb }) {
  const [open, setOpen] = useState(false);
  const flagged = (r.checklist || []).filter((c) => c.status === "Needs attention").length;
  return (
    <Card data-testid="report-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
        <div><span className="lbl-m">{r.visit_date || ""} · {r.author}</span><h3 style={{ fontSize: 26, margin: "6px 0 8px" }}>{r.title}</h3></div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
          <StatusPill status={r.status} />
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/${r.report_id}/pdf`} target="_blank" rel="noreferrer" className="link-u" data-testid="report-pdf-btn" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 6 }}><Download size={13} /> PDF</a>
        </div>
      </div>
      <p style={{ color: "var(--muted)", fontSize: 15 }}>{r.summary}</p>
      {r.recommendations && <div style={{ background: "var(--alt)", padding: 12, borderRadius: 2, marginTop: 10, fontSize: 14 }}><span className="lbl-m">Recommendations</span><p style={{ marginTop: 4 }}>{r.recommendations}</p></div>}
      {r.checklist?.length > 0 && (
        <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 12 }}>
          <button onClick={() => setOpen((o) => !o)} data-testid="report-checklist-toggle" style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
            <span className="lbl-m">Inspection checklist · {r.checklist.length} checkpoints{flagged ? ` · ${flagged} need attention` : ""}</span>
            <ChevronDown size={18} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", color: "var(--muted)" }} />
          </button>
          {open && (
            <div style={{ marginTop: 10 }} data-testid="report-checklist-body">
              {r.checklist.map((c, idx) => { const ok = c.status === "OK" || c.status === "Good"; return (
                <div key={idx} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", fontSize: 13, padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
                  <span>{c.group ? <span style={{ color: "var(--honey)" }}>{c.group} · </span> : ""}{c.area}{c.note ? <em style={{ color: "var(--muted)" }}>, {c.note}</em> : ""}</span>
                  <span className="pill-tag" style={{ background: ok ? "#e7ecdf" : c.status === "Needs attention" ? "#f3e4cf" : "#e6e2da", color: ok ? "#5B6440" : c.status === "Needs attention" ? "#8a5a1f" : "#726A5C", flexShrink: 0 }}>{c.status}</span>
                </div>
              ); })}
            </div>
          )}
        </div>
      )}
      {r.photos?.length > 0 && (() => { const urls = r.photos.map((ph) => typeof ph === "string" ? ph : ph.url); return (
        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          {r.photos.map((ph, idx) => { const u = typeof ph === "string" ? ph : ph.url; const a = typeof ph === "string" ? null : ph.area; return (
            <div key={idx} style={{ width: 130 }}>
              <img src={u} alt="" onClick={() => setLb({ images: urls, index: idx })} style={{ width: 130, height: 90, objectFit: "cover", borderRadius: 2, display: "block", cursor: "pointer" }} />
              {a && <div className="lbl-m" style={{ marginTop: 4, fontSize: 9 }}>{a}</div>}
            </div>
          ); })}
        </div>
      ); })()}
    </Card>
  );
}

function ClientHealthChecks({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div style={{ display: "grid", gap: 18 }} data-testid="client-health-checks">
      <div><div className="lbl" style={{ marginBottom: 6 }}>Property health check</div>
        <p style={{ color: "var(--muted)", fontSize: 14, margin: 0 }}>Our complimentary, non-intrusive review of your property's condition and the systems worth keeping an eye on.</p></div>
      {(items || []).length === 0 && <p style={{ color: "var(--muted)" }}>No health check has been shared yet.</p>}
      {(items || []).map((h) => {
        const flagged = (h.checklist || []).filter((c) => c.status === "Needs attention" || c.status === "Urgent");
        const refs = (h.checklist || []).filter((c) => c.needs_contractor);
        const isOpen = open === h.health_check_id;
        return (
          <Card key={h.health_check_id} data-testid="client-health-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
              <div><span className="lbl-m">{h.visit_date || ""} · {h.author}</span><h3 style={{ fontSize: 24, margin: "6px 0 4px" }}>Health check</h3>
                <div style={{ fontSize: 13, color: "var(--muted)" }}>Overall: {h.condition || "Good"} · {flagged.length} item(s) flagged · {refs.length} we can arrange for you</div></div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/health-checks/${h.health_check_id}/pdf`} target="_blank" rel="noreferrer" className="link-u" data-testid="client-health-pdf" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}><Download size={13} /> PDF</a>
            </div>
            {h.summary && <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 10 }}>{h.summary}</p>}
            {h.recommendations && <div style={{ background: "var(--alt)", padding: 12, borderRadius: 2, marginTop: 8, fontSize: 14 }}><span className="lbl-m">Recommendations</span><p style={{ marginTop: 4 }}>{h.recommendations}</p></div>}
            {refs.length > 0 && (
              <div style={{ background: "#f6f1e8", border: "1px solid #e6ddcb", padding: 14, borderRadius: 6, marginTop: 12 }}>
                <span className="lbl-m" style={{ color: "#8a5a1f" }}>We can arrange these for you</span>
                <div style={{ marginTop: 6, fontSize: 14 }}>{refs.map((c, i) => <div key={i}>• {c.area}{c.trade ? ` — ${c.trade}` : ""}</div>)}</div>
              </div>
            )}
            {(h.checklist || []).length > 0 && (
              <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 12 }}>
                <button onClick={() => setOpen(isOpen ? null : h.health_check_id)} data-testid="client-health-toggle" style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  <span className="lbl-m">Full findings · {h.checklist.length} checkpoints{flagged.length ? ` · ${flagged.length} flagged` : ""}</span>
                  <ChevronDown size={18} style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform .2s", color: "var(--muted)" }} />
                </button>
                {isOpen && <div style={{ marginTop: 10 }}>{h.checklist.map((c, i) => { const ok = c.status === "Good" || c.status === "Not applicable"; return (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", fontSize: 13, padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
                    <span><span style={{ color: "var(--honey)" }}>{c.group} · </span>{c.area}{c.note ? <em style={{ color: "var(--muted)" }}>, {c.note}</em> : ""}</span>
                    <span className="pill-tag" style={{ background: ok ? "#e7ecdf" : c.status === "Urgent" ? "#f4ddd9" : "#f3e4cf", color: ok ? "#5B6440" : c.status === "Urgent" ? "#a13529" : "#8a5a1f", flexShrink: 0 }}>{c.status}</span>
                  </div>
                ); })}</div>}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function Reports({ reports }) {
  const [lb, setLb] = useState(null);
  return (
    <div style={{ display: "grid", gap: 18 }}>
      {reports.map((r) => <ReportCard key={r.report_id} r={r} setLb={setLb} />)}
      {reports.length === 0 && <p style={{ color: "var(--muted)" }}>No reports yet.</p>}
      {lb && <Lightbox images={lb.images} index={lb.index} onClose={() => setLb(null)} onIndex={(i) => setLb((s) => ({ ...s, index: i }))} />}
    </div>
  );
}

function Media({ reports }) {
  const all = reports.flatMap((r) => (r.photos || []).map((ph) => typeof ph === "string" ? { url: ph, area: "General" } : { url: ph.url, area: ph.area || "General" }));
  const [sel, setSel] = useState("All");
  const [lb, setLb] = useState(null);
  if (!all.length) return <p style={{ color: "var(--muted)" }}>No media yet.</p>;
  const areas = ["All", ...Array.from(new Set(all.map((p) => p.area)))];
  const shown = sel === "All" ? all : all.filter((p) => p.area === sel);
  const groups = shown.reduce((acc, p) => { (acc[p.area] = acc[p.area] || []).push(p); return acc; }, {});
  const shownUrls = shown.map((p) => p.url);
  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
        {areas.map((a) => <button key={a} onClick={() => setSel(a)} data-testid={`media-filter-${a.replace(/[^a-z]/gi, "").toLowerCase()}`} style={{ cursor: "pointer", padding: "7px 14px", fontSize: 12, letterSpacing: ".04em", border: `1px solid ${sel === a ? "var(--ink)" : "var(--line)"}`, background: sel === a ? "var(--ink)" : "var(--soft)", color: sel === a ? "var(--paper)" : "var(--ink)" }}>{a}</button>)}
      </div>
      <div style={{ display: "grid", gap: 32 }}>
        {Object.keys(groups).map((k) => (
          <div key={k}>
            <div className="lbl" style={{ marginBottom: 14 }}>{k} · {groups[k].length}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 12 }}>
              {groups[k].map((p, i) => <div key={i} className="hover-lift" style={{ overflow: "hidden", borderRadius: 2 }}><img src={p.url} alt="" onClick={() => setLb({ images: shownUrls, index: shownUrls.indexOf(p.url) })} style={{ width: "100%", height: 160, objectFit: "cover", display: "block", cursor: "pointer" }} /></div>)}
            </div>
          </div>
        ))}
      </div>
      {lb && <Lightbox images={lb.images} index={lb.index} onClose={() => setLb(null)} onIndex={(i) => setLb((s) => ({ ...s, index: i }))} />}
    </div>
  );
}

function Twin({ prop }) {
  if (!prop?.matterport_id) return <Card><p style={{ color: "var(--muted)" }}>Your digital twin will appear here once your Matterport scan is complete.</p></Card>;
  return (
    <div>
      <p style={{ color: "var(--muted)", marginBottom: 16, maxWidth: "56ch" }}>Walk your home in 3D, from anywhere. Click and drag to explore.</p>
      <div style={{ position: "relative", paddingTop: "56%", borderRadius: 2, overflow: "hidden", boxShadow: "0 30px 70px -50px rgba(33,29,24,.6)" }}>
        <iframe title="Matterport digital twin" src={`https://my.matterport.com/show/?m=${prop.matterport_id}&play=1`} frameBorder="0" allowFullScreen allow="xr-spatial-tracking" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
      </div>
    </div>
  );
}

function Documents({ documents }) {
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {documents.map((d) => (
        <a key={d.document_id} href={d.url} target="_blank" rel="noreferrer" className="card-soft hover-lift" style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div><span className="lbl-m">{d.category}</span><div style={{ fontSize: 16, marginTop: 4 }}>{d.title}</div></div>
          <span className="link-u">Open</span>
        </a>
      ))}
      {documents.length === 0 && <p style={{ color: "var(--muted)" }}>No documents yet.</p>}
    </div>
  );
}

function Arrivals({ arrivals, addArrival, cancelArrival, editArrival }) {
  const EMPTY = { arrival_date: "", departure_date: "", guests: 2, guest_names: "", flight_number: "", arrival_time: "", departure_flight: "", departure_time: "", transfer_requested: false, transfer_passengers: "", transfer_notes: "", notes: "" };
  const [f, setF] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const submit = (e) => { e.preventDefault(); if (editId) { editArrival(editId, f); setEditId(null); } else { addArrival(f); } setF(EMPTY); };
  return (
    <div style={{ display: "grid", gap: 24 }}>
      <Card>
        <div className="lbl">{editId ? "Amend trip" : "Plan a trip"}</div>
        <form onSubmit={submit} className="trip-form" style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>Arrival<input className="inp" type="date" value={f.arrival_date} onChange={(e) => setF({ ...f, arrival_date: e.target.value })} required /></label>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>Departure<input className="inp" type="date" value={f.departure_date} onChange={(e) => setF({ ...f, departure_date: e.target.value })} required /></label>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>Flight number<input className="inp" placeholder="e.g. BA2604" value={f.flight_number} onChange={(e) => setF({ ...f, flight_number: e.target.value })} data-testid="arrival-flight-input" /></label>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>Arrival time<input className="inp" type="time" value={f.arrival_time} onChange={(e) => setF({ ...f, arrival_time: e.target.value })} data-testid="arrival-time-input" /></label>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>Return flight<input className="inp" placeholder="e.g. BA2605" value={f.departure_flight} onChange={(e) => setF({ ...f, departure_flight: e.target.value })} data-testid="departure-flight-input" /></label>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>Return time<input className="inp" type="time" value={f.departure_time} onChange={(e) => setF({ ...f, departure_time: e.target.value })} data-testid="departure-time-input" /></label>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>Guests<input className="inp" type="number" min={1} value={f.guests} onChange={(e) => setF({ ...f, guests: Number(e.target.value) })} /></label>
          <label style={{ gridColumn: "1 / -1", fontSize: 12, color: "var(--muted)" }}>Guest names<input className="inp" placeholder="e.g. James & Sarah Brooks, +2 children" value={f.guest_names} onChange={(e) => setF({ ...f, guest_names: e.target.value })} data-testid="arrival-guest-names-input" /></label>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>Notes<input className="inp" placeholder="e.g. please bring the car to the airport" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="arrival-notes-input" /></label>
          <label style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={f.transfer_requested} onChange={(e) => setF({ ...f, transfer_requested: e.target.checked })} data-testid="transfer-check" style={{ width: 16, height: 16 }} />
            Request an airport transfer / chauffeur for arrival
          </label>
          {f.transfer_requested && (<>
            <label style={{ fontSize: 12, color: "var(--muted)" }}>Passengers<input className="inp" type="number" min={1} value={f.transfer_passengers} onChange={(e) => setF({ ...f, transfer_passengers: e.target.value ? Number(e.target.value) : "" })} data-testid="transfer-passengers-input" /></label>
            <label style={{ fontSize: 12, color: "var(--muted)" }}>Pickup notes<input className="inp" placeholder="e.g. meet at arrivals hall" value={f.transfer_notes} onChange={(e) => setF({ ...f, transfer_notes: e.target.value })} data-testid="transfer-notes-input" /></label>
          </>)}
          <button className="btn-ink" style={{ gridColumn: "1 / -1" }} type="submit" data-testid="add-arrival-btn">{editId ? "Save changes" : "Add trip"}</button>
        </form>
        <style>{`.trip-form{display:grid;grid-template-columns:1fr 1fr;gap:12px}.trip-form label{min-width:0}.trip-form .inp{width:100%;box-sizing:border-box;min-width:0;display:block;margin-top:4px}@media(max-width:560px){.trip-form{grid-template-columns:1fr!important}}`}</style>
      </Card>
      <div style={{ display: "grid", gap: 12 }}>
        {arrivals.map((a) => (
          <Card key={a.arrival_id}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <b>{a.arrival_date} → {a.departure_date}</b><span style={{ color: "var(--muted)" }}>{a.guests} guests</span>
            </div>
            {a.guest_names && <div style={{ fontSize: 13, color: "var(--umber)", marginTop: 6 }}>{a.guest_names}</div>}
            {(a.flight_number || a.arrival_time) && (
              <div style={{ fontSize: 13, color: "var(--umber)", marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <Plane size={14} style={{ color: "var(--honey)" }} />
                {a.flight_number ? `Flight ${a.flight_number}` : "Flight"}{a.arrival_time ? ` · arriving ${a.arrival_time}` : ""}
              </div>
            )}
            {(a.departure_flight || a.departure_time) && (
              <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4, display: "flex", alignItems: "center", gap: 8 }}>
                <Plane size={14} style={{ color: "var(--muted)", transform: "scaleX(-1)" }} />
                Return {a.departure_flight ? a.departure_flight : ""}{a.departure_time ? ` · departing ${a.departure_time}` : ""}
              </div>
            )}
            {a.notes && <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 6 }}>{a.notes}</p>}
            {a.transfer_requested && <div style={{ fontSize: 13, color: "var(--umber)", marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}><Car size={14} style={{ color: "var(--honey)" }} /> Airport transfer requested{a.transfer_passengers ? ` · ${a.transfer_passengers} pax` : ""}</div>}
            {a.chauffeur_quote && (
              <div style={{ marginTop: 10, padding: "11px 13px", background: "#f6f1e8", border: "1px solid #e6ddcb", borderRadius: 6 }} data-testid="client-chauffeur-quote">
                <div style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}><Car size={15} style={{ color: "var(--honey)" }} /> Chauffeur service: <b>€{Number(a.chauffeur_quote.amount).toFixed(2)}</b></div>
                {a.chauffeur_quote.note && <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{a.chauffeur_quote.note}</div>}
                {a.chauffeur_quote.status === "quoted" ? (
                  <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                    <button className="btn-ink btn-olive" style={{ padding: "5px 14px", fontSize: 13 }} onClick={() => editArrival(a.arrival_id, { chauffeur_quote: { ...a.chauffeur_quote, status: "accepted" } })} data-testid="accept-chauffeur-btn">Accept</button>
                    <button className="btn-outline" style={{ padding: "5px 14px", fontSize: 13 }} onClick={() => editArrival(a.arrival_id, { chauffeur_quote: { ...a.chauffeur_quote, status: "declined" } })} data-testid="decline-chauffeur-btn">Decline</button>
                  </div>
                ) : <div style={{ fontSize: 12, color: a.chauffeur_quote.status === "accepted" ? "#5B6440" : "var(--muted)", marginTop: 6, textTransform: "capitalize" }}>{a.chauffeur_quote.status}</div>}
              </div>
            )}
            {Array.isArray(a.checklist) && a.checklist.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }} data-testid="client-prep-progress">Pre-arrival prep · {a.checklist.filter((c) => c.done).length}/{a.checklist.length} done</div>
            )}
            <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
              <button className="link-u" onClick={() => { setEditId(a.arrival_id); setF({ arrival_date: a.arrival_date, departure_date: a.departure_date, guests: a.guests, guest_names: a.guest_names || "", flight_number: a.flight_number || "", arrival_time: a.arrival_time || "", departure_flight: a.departure_flight || "", departure_time: a.departure_time || "", transfer_requested: !!a.transfer_requested, transfer_passengers: a.transfer_passengers || "", transfer_notes: a.transfer_notes || "", notes: a.notes || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="edit-trip-btn" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--honey)", fontSize: 12 }}>Edit</button>
              <button className="link-u" onClick={() => cancelArrival(a.arrival_id)} data-testid="cancel-trip-btn" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", fontSize: 12 }}>Cancel trip</button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Issues({ issues, raiseIssue, decide }) {
  const [f, setF] = useState({ title: "", description: "" });
  const [photos, setPhotos] = useState([]);
  const submit = (e) => { e.preventDefault(); if (!f.title) return; raiseIssue(f.title, f.description, photos); setF({ title: "", description: "" }); setPhotos([]); };
  return (
    <div style={{ display: "grid", gap: 24 }}>
      {issues.length > 0 && (
        <div style={{ display: "grid", gap: 24 }} data-testid="client-issue-list">
          {issues.map((i) => (
            <Card key={i.issue_id} data-testid="issue-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <h3 style={{ fontSize: 22 }}>{i.title}</h3><StatusPill status={i.status} />
              </div>
              <p style={{ color: "var(--muted)", fontSize: 14, margin: "8px 0" }}>{i.description}</p>
              {i.photos?.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  {i.photos.map((ph, idx) => { const u = typeof ph === "string" ? ph : ph.url; return <img key={idx} src={u} alt="" style={{ width: 88, height: 66, objectFit: "cover", borderRadius: 2 }} />; })}
                </div>
              )}
              {i.staff_note && <div style={{ background: "var(--alt)", padding: 14, borderRadius: 2, fontSize: 14 }}><span className="lbl-m">Solvana update</span><p style={{ marginTop: 6 }}>{i.staff_note}</p></div>}
              {i.quote_amount && !i.approval && (
                <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 14 }}>
                  <b>Quote: €{i.quote_amount}</b>
                  <button className="btn-ink btn-olive" data-testid="approve-btn" onClick={() => decide(i.issue_id, true)}>Approve</button>
                  <button className="btn-outline" onClick={() => decide(i.issue_id, false)}>Decline</button>
                </div>
              )}
              {i.approval && <div style={{ marginTop: 10 }}><StatusPill status={i.approval} /></div>}
            </Card>
          ))}
        </div>
      )}
      <Card>
        <div className="lbl">Raise an issue or request</div>
        <form onSubmit={submit} style={{ display: "grid", gap: 12, marginTop: 12 }}>
          <input className="inp" placeholder="Short title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required />
          <textarea className="inp" rows={3} placeholder="Describe what you need" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <PhotoUploader value={photos} onChange={setPhotos} label="Add photos (optional)" withAreas />
          <button className="btn-ink" type="submit" data-testid="raise-issue-btn" style={{ justifySelf: "start" }}>Submit</button>
        </form>
      </Card>
    </div>
  );
}

function Messages({ messages, sendMsg, markAllRead }) {
  const [text, setText] = useState("");
  const submit = (e) => { e.preventDefault(); if (!text.trim()) return; sendMsg(text); setText(""); };
  const fmtMsg = (d) => { const dt = new Date(d); return isNaN(dt.getTime()) ? "" : dt.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); };
  return (
    <div style={{ maxWidth: 720 }}>
      {messages.length > 0 && <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}><button className="link-u" onClick={markAllRead} data-testid="mark-all-read-btn" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", fontSize: 12 }}>Mark all as read</button></div>}
      <div style={{ display: "grid", gap: 14, marginBottom: 20 }}>
        {messages.map((m) => {
          const staff = m.sender_role !== "client";
          return (
            <div key={m.message_id} style={{ display: "flex", flexDirection: "column", alignItems: staff ? "flex-start" : "flex-end" }}>
              <div style={{ padding: "11px 15px", borderRadius: staff ? "14px 14px 14px 4px" : "14px 14px 4px 14px", background: staff ? "var(--soft)" : "var(--ink)", color: staff ? "var(--ink)" : "var(--paper)", border: staff ? "1px solid var(--line)" : "none", maxWidth: "80%", fontSize: 14, lineHeight: 1.5 }}>{m.body}</div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>{staff ? (m.sender_name || "Solvana") : "You"} · {fmtMsg(m.created_at)}</div>
            </div>
          );
        })}
        {messages.length === 0 && <p style={{ color: "var(--muted)" }}>No messages yet, say hello to your team.</p>}
      </div>
      <form onSubmit={submit} style={{ display: "flex", gap: 10 }}>
        <input className="inp" placeholder="Write a message…" value={text} onChange={(e) => setText(e.target.value)} data-testid="message-input" />
        <button className="btn-ink" type="submit" data-testid="send-message-btn">Send</button>
      </form>
    </div>
  );
}

function Invoices({ invoices, payInvoice }) {
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {invoices.map((inv) => (
        <Card key={inv.invoice_id} data-testid="invoice-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14 }}>
            <div>
              <div style={{ fontSize: 17 }}>{inv.description}</div>
              <div className="lbl-m" style={{ marginTop: 4 }}>Due {inv.due_date || "-"}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="font-serif-c" style={{ fontSize: 30 }}>€{Number(inv.amount).toLocaleString()}</div>
              <div style={{ marginTop: 6 }}><StatusPill status={inv.status} /></div>
            </div>
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {inv.status === "unpaid" && <button className="btn-ink btn-olive" data-testid="pay-invoice-btn" onClick={() => payInvoice(inv)}>Pay securely</button>}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.invoice_id}/pdf`} target="_blank" rel="noreferrer" className="btn-outline" data-testid="invoice-pdf-btn" style={{ padding: "8px 16px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}><Download size={15} /> View / download PDF</a>
          </div>
        </Card>
      ))}
      {invoices.length === 0 && <p style={{ color: "var(--muted)" }}>No invoices yet.</p>}
    </div>
  );
}
